// Reopening an MSQA form after the phone reloads WRAP (row #7, 2026-10-16).
//
// "Add photo" hands the phone to the camera app. On a phone short of memory
// the browser discards the WRAP tab while the camera is in front and reloads
// it on return: the open form, the item and the photo's `change` event are
// all gone, and the monitor lands on the map. msqa.ts records which form is
// open (and which item is waiting for a photo) here, and on the next start the
// field app reopens that form at that item and says the photo didn't arrive.
// Pure functions over a Storage, so frontend/tests/msqaResume.test.ts runs in
// plain Node.

export const RESUME_KEY = "wrap.msqa.open";
/** Older than this, a remembered form is not reopened (the monitor has moved on). */
export const RESUME_MAX_AGE_MS = 6 * 60 * 60 * 1000;

export interface ResumeState {
  form_id: string;
  project_id: string;
  owner: string | null;
  /** The item card last worked on (scroll target). */
  item: string | null;
  /** Set when "Add photo" / "Choose from library" handed over to the camera or picker. */
  photo_pending: boolean;
  at: number;
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function saveResume(store: Store | null, s: Omit<ResumeState, "at">, now = Date.now()): void {
  try {
    store?.setItem(RESUME_KEY, JSON.stringify({ ...s, at: now }));
  } catch {
    /* storage blocked / full: nothing to reopen after a reload */
  }
}

export function clearResume(store: Store | null): void {
  try {
    store?.removeItem(RESUME_KEY);
  } catch {
    /* storage blocked */
  }
}

/** The form to reopen, or null (none, malformed, someone else's, or stale). */
export function readResume(store: Store | null, owner: string | null, now = Date.now()): ResumeState | null {
  let raw: string | null = null;
  try {
    raw = store?.getItem(RESUME_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  let s: Partial<ResumeState>;
  try {
    s = JSON.parse(raw) as Partial<ResumeState>;
  } catch {
    return null;
  }
  if (!s || typeof s.form_id !== "string" || typeof s.project_id !== "string" || typeof s.at !== "number") return null;
  if ((s.owner ?? null) !== owner) return null;
  if (now - s.at > RESUME_MAX_AGE_MS || s.at > now + 60_000) return null;
  return { form_id: s.form_id, project_id: s.project_id, owner: s.owner ?? null, item: typeof s.item === "string" ? s.item : null,
           photo_pending: s.photo_pending === true, at: s.at };
}
